import { isOidcFlowTypeSupported, OIDC_FLOW_TYPE_SUPPORT, SignupFlowType } from '@signup/shared';
import { SignupParams } from './ParseQueryParams';

/**
 * Whether OIDC (Google / Microsoft) signup can honor a given URL param or signup flow type.
 *
 * Until SNOW-4313138 lands a real design, OIDC fails closed: if a signup relies on something OIDC
 * cannot carry, the OIDC buttons are hidden rather than dropping the user into a degraded regular
 * signup (e.g. a sales-assisted link that silently becomes a plain self-service trial).
 *
 * Closed over the KNOWN functional params and flow types, enforced at compile time by the Record
 * types below. It is deliberately NOT closed over arbitrary query keys: utm_*, gclid, fbclid, _ga
 * and friends ride on a large share of normal traffic and must never hide the buttons.
 *
 *  - handled:   OIDC carries it end to end (see OidcSignupContext in SocialLoginButtons.tsx and
 *               validateMechanicalParams in api/src/controllers/oidcSignupController.ts).
 *  - ignorable: dropping it changes nothing the user or the account would notice.
 *  - blocking:  the regular flow acts on it and OIDC does not; hide the buttons when it is present.
 */
export type OidcDisposition = 'handled' | 'ignorable' | 'blocking';

// Keyed by SignupParams (the parsed-param interface), so adding a param there without deciding how
// OIDC treats it fails tsc.
//
// The guarantee stops there: it is exhaustive over keyof SignupParams and SignupFlowType, not over
// every query key the API reads. A param the API reads straight from req.query without being added
// to SignupParams does not trip tsc. `trial` is the existing example; it is covered only because it
// maps to flow types (Startup, Student, Learn, Streamlit). A new param of that kind needs a manual
// decision here.
export const OIDC_PARAM_DISPOSITION: Record<keyof SignupParams, OidcDisposition> = {
  // Prefill only: SignupFormDataContext seeds edition/cloud/region from these and OIDC reads the
  // edition/cloud/region from that same form state at click time, so the choice is still honored.
  plan: 'ignorable',
  planp: 'ignorable',
  cloud: 'ignorable',
  cloudp: 'ignorable',
  region: 'ignorable',
  regionp: 'ignorable',
  // Prefill only. The IdP supplies the identity OIDC uses, so a prefilled email that differs from
  // the IdP email is simply not used (the account is created for the IdP identity).
  firstname: 'ignorable',
  lastname: 'ignorable',
  email: 'ignorable',
  // The server already applies ?abexperiment= at page load (abExperimentContext.ts sets the override
  // cookie), which is what decides whether OIDC renders at all. The copy in the signup request body
  // is a legacy no-op override.
  abexperiment: 'ignorable',
  // Declared on SignupParams and forwarded by signupApi.ts when truthy, but parseQueryParams has no
  // handler for it (so it is never set), it is not a SignupUrlParameter, and nothing in api/ or
  // shared/ reads it. Vestigial.
  k: 'ignorable',
  // Carried in OidcSignupContext and validated in validateMechanicalParams.
  template: 'handled',
  lab: 'handled',
  workspaces: 'handled',
  account: 'handled',
  token: 'handled',
  E6CCEE4CEB: 'handled',
  referrer: 'handled',
  owner: 'handled',
  sdm: 'handled',
  // Sales-assisted (eng): drives the SE/AE emails, se_email/se_ldap account params and the Salesforce
  // sales-assist fields. OIDC passes none of it (isSalesAssistedFlow is false for every OIDC signup).
  eng: 'blocking',
  // AWS Marketplace token / account. The regular flow only forwards ampt when an AWS path set
  // awsType, so on `/` it is dropped there too; blocked anyway because a link carrying it is
  // expressing Marketplace intent that OIDC never fulfils.
  ampt: 'blocking',
  awsmp: 'blocking',
  // Marketplace listing: custom landing page, Salesforce data-exchange attribution, VA2 region rule.
  // OIDC does not carry it.
  listing: 'blocking',
  // CoCo subscription promo: program approval check and claim. OIDC does neither.
  program: 'blocking',
  // Event signup link token: needs a guaranteed new account plus link-driven entitlement. OIDC does
  // not carry it and its existing-account picker defeats the purpose (SNOW-4306083).
  t: 'blocking',
};

// Defined in @signup/shared so the API enforces the same allowlist the UI uses to hide the buttons.
// Re-exported so existing imports of it from this module keep working.
export { OIDC_FLOW_TYPE_SUPPORT };

const BLOCKING_PARAMS = (Object.keys(OIDC_PARAM_DISPOSITION) as (keyof SignupParams)[]).filter(
  param => OIDC_PARAM_DISPOSITION[param] === 'blocking',
);

/**
 * Returns why OIDC cannot honor this signup, or an empty array when it can.
 *
 * Param presence is decided from the raw query string keys, not from parseQueryParams, because the
 * API derives the signup flow type from key presence (`Object.keys(req.query)`), so a valueless
 * `?eng` makes the signup sales-assisted even though parseQueryParams drops it. Keys are matched
 * case-sensitively, as the API does, and a bracketed key (`eng[]`, `eng[x]`) counts as `eng` because
 * that is the key Express's query parser produces for it.
 */
export function getOidcUnsupportedReasons(
  rawQueryString: string,
  signupFlowType: SignupFlowType,
): string[] {
  const presentKeys = new Set<string>();
  for (const key of new URLSearchParams(rawQueryString).keys()) {
    presentKeys.add(key.replace(/\[.*$/, ''));
  }

  const reasons = BLOCKING_PARAMS.filter(param => presentKeys.has(param)).map(
    param => `param:${param}`,
  );
  if (!isOidcFlowTypeSupported(signupFlowType)) {
    reasons.push(`flowType:${signupFlowType}`);
  }
  return reasons;
}
